import {Attributes, AttributesRow, Card} from 'platform/components';
import {Link} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';

import {isEmpty, isNil} from 'ramda';

import {PaymentTypeResponseBody} from '@dms/api/core';
import {BaseAuthorizationProfile} from '@dms/api/security';
import {CurrencyResponseBody} from '@dms/api/shared';
import {
  BaseSupplier,
  GetReceiveNoteCorrectionResponse,
  GetWarehousesResponse,
} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {warehouseRoutes} from '@dms/routes';

import {composePath, EMPTY_PLACEHOLDER, Nullish, parseDate, RequiredTestIdProps} from 'shared/web';

interface OriginalCardProps extends RequiredTestIdProps {
  receiveNoteCorrection: GetReceiveNoteCorrectionResponse | Nullish;
  suppliers: BaseSupplier[] | Nullish;
  warehouses: GetWarehousesResponse | Nullish;
  authorizationProfiles: BaseAuthorizationProfile[] | Nullish;
  paymentTypes: PaymentTypeResponseBody[] | Nullish;
  currencies: CurrencyResponseBody[] | Nullish;
}

export function OriginalCard(props: OriginalCardProps) {
  const dateTimeFormatter = useDateTimeFormatter();

  const receiveNoteNumber = props.receiveNoteCorrection?.receiveNote?.receiveNoteNumber;

  const originalCompletedAt = props.receiveNoteCorrection?.receiveNote?.completedAt;

  const originalInvoiceNumber = props.receiveNoteCorrection?.receiveNote?.invoiceNumber;

  const originalInvoiceIssueDate = props.receiveNoteCorrection?.receiveNote?.invoiceIssueDate;

  const originalDeliveryNoteNumber = props.receiveNoteCorrection?.receiveNote?.deliveryNoteNumber;

  const originalDeliveryNoteIssueDate =
    props.receiveNoteCorrection?.receiveNote?.deliveryNoteIssueDate;

  const originalNote = props.receiveNoteCorrection?.receiveNote?.note;

  const formatToShortDate = (date: string | Nullish) => {
    if (isNil(date) || isEmpty(date)) {
      return EMPTY_PLACEHOLDER;
    }
    return dateTimeFormatter('dateShort', parseDate(date));
  };

  const receiveNoteId = props.receiveNoteCorrection?.receiveNote?.receiveNoteId;

  const rows: AttributesRow[] = [
    {
      label: i18n.t('entity.warehouse.labels.number'),
      content: props.receiveNoteCorrection?.receiveNote?.receiveNoteNumber ? (
        <Link
          href={composePath(warehouseRoutes.receiveNoteDetailOverview, {
            params: {id: receiveNoteId},
            isSearchParamsPreserved: false,
          })}
          size="small"
          target="_blank"
          rightIcon="action/open_in_new"
        >
          {receiveNoteNumber ?? EMPTY_PLACEHOLDER}
        </Link>
      ) : (
        EMPTY_PLACEHOLDER
      ),
    },
    {
      label: i18n.t('general.labels.completedAt'),
      value: formatToShortDate(originalCompletedAt),
    },
    {
      label: i18n.t('entity.warehouse.labels.invoiceNumber'),
      value: originalInvoiceNumber,
    },
    {
      label: i18n.t('entity.warehouse.labels.invoiceIssueDate'),
      value: formatToShortDate(originalInvoiceIssueDate),
    },
    {
      label: i18n.t('entity.warehouse.labels.deliveryNoteNumber'),
      value: originalDeliveryNoteNumber,
    },
    {
      label: i18n.t('entity.warehouse.labels.deliveryNoteIssueDate'),
      value: formatToShortDate(originalDeliveryNoteIssueDate),
    },
    {
      label: i18n.t('entity.warehouse.labels.note'),
      value: originalNote,
    },
  ];

  return (
    <Card variant="inlineGrey">
      <Attributes size="quarter" rows={rows} />
    </Card>
  );
}
